<template>
  <InfusePageLayout>
    <template #top-bar>
      <NavigationTopBar />
    </template>

    <div class="flex flex-row gap-4">
      <div class="flex h-16 w-16 items-center justify-center rounded-md border border-neutral-400">
        <font-awesome-icon class="text-4xl" :icon="['fal', 'cubes']" />
      </div>
      <div>
        <h2 class="font-bold">
          {{ tenant?.name }}
        </h2>
        <p class="text-sm">
          {{ tenant?.description }}
        </p>
      </div>

      <div class="ml-auto">
        <InfuseButton
          v-if="tenant?.active"
          color="alter"
          @click="onUpdateActive(false)"
          :disabled="isLoading"
          v-permission="{ id: tenantPermissions.DEACTIVATE }"
        >
          {{ t('iam.tenants.header.deactivate_tenant') }}
        </InfuseButton>
        <InfuseButton
          v-else
          variant="primary-border"
          @click="onUpdateActive(true)"
          :disabled="isLoading"
          v-permission="{ id: tenantPermissions.ACTIVATE }"
        >
          {{ t('iam.tenants.header.activate_tenant') }}
        </InfuseButton>
      </div>
    </div>

    <div class="flex flex-col gap-6 overflow-y-auto">
      <InfuseRouterTabLayout :model="tenant" />
    </div>

    <InfuseConfirmModal
      v-if="tenant && isActiveModalOpen"
      :title="t('iam.tenants.header.activate_tenant')"
      :message="t('iam.tenants.info.activate_tenant')"
      verification-text="activate"
      :loading="isActivatePending"
      @confirm="onActivateTenant"
      @close="isActiveModalOpen = false"
    />

    <InfuseConfirmModal
      v-if="tenant && isDeactiveModalOpen"
      :title="t('iam.tenants.header.deactivate_tenant')"
      :message="t('iam.tenants.info.deactivate_tenant')"
      verification-text="deactivate"
      :loading="isDeactivatePending"
      @confirm="onDeactivateTenant"
      @close="isDeactiveModalOpen = false"
    />
  </InfusePageLayout>

  <router-view v-slot="{ Component }" name="slideIn">
    <transition
      enter-active-class="duration-300 ease-out"
      enter-from-class="translate-x-full"
      leave-active-class="duration-200 ease-in"
      leave-to-class="translate-x-full"
    >
      <component :is="Component" />
    </transition>
  </router-view>
</template>

<script setup lang="ts">
import { InfuseButton, InfuseConfirmModal } from '@infuse/ui/components';
import { useActivateTenant, useDeactivateTenant } from '@infuse/iam/tenant/mutations';
import { useTenant } from '@infuse/iam/tenant/queries';
import { InfusePageLayout, InfuseRouterTabLayout, NavigationTopBar } from '@infuse/ui/layouts';
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { vPermission } from '@infuse/iam/permission/directives';
import { tenantPermissions } from '@infuse/api/iam/tenant';

const { t } = useI18n();
const route = useRoute();

const { tenant, fetchTenantId, isLoading: isTenantLoading } = useTenant();
const { mutateAsync: activateTenant, isPending: isActivatePending } = useActivateTenant();
const { mutateAsync: deactivateTenant, isPending: isDeactivatePending } = useDeactivateTenant();

const isLoading = computed(() => isTenantLoading.value || isActivatePending.value || isDeactivatePending.value);

const isActiveModalOpen = ref(false);
const isDeactiveModalOpen = ref(false);

watch(
  route,
  () => {
    fetchTenantId.value = route.params.id as string;
  },
  { immediate: true }
);

function onUpdateActive(value: boolean) {
  if (!tenant.value?.id) {
    return;
  }

  if (value) {
    isActiveModalOpen.value = true;
    isDeactiveModalOpen.value = false;
  } else {
    isActiveModalOpen.value = false;
    isDeactiveModalOpen.value = true;
  }
}

async function onActivateTenant() {
  if (!tenant.value?.id) {
    return;
  }

  await activateTenant(tenant.value.id);
  isActiveModalOpen.value = false;
}

async function onDeactivateTenant() {
  if (!tenant.value?.id) {
    return;
  }

  await deactivateTenant(tenant.value?.id);
  isDeactiveModalOpen.value = false;
}
</script>
